/* Legal move generation (before the ko check, which needs game history; see game.ts). */
import { SHAPES, vecOf } from './shapes.ts';
import { bodyOf, cellOf, inb } from './position.ts';
import type { Position } from './position.ts';
import { MAX_PIECES } from './types.ts';
import type { Move, Side } from './types.ts';

/** Every cell each member of `body` passes through must be free (or another member's), except that a member may leave the board. */
function bodyPathClear(p: Position, body: readonly number[], dr: number, dc: number, d: number): boolean {
  for (const m of body) {
    for (let k = 1; k <= d; k++) {
      const rr = p.row[m] + dr * k, cc = p.col[m] + dc * k;
      if (!inb(rr, cc)) break;                       // this member falls off the board: allowed
      const q = p.board[cellOf(rr, cc)];
      if (q >= 0 && !body.includes(q)) return false;
    }
  }
  return true;
}

/** Moves for one piece: move its whole body along the piece's pattern, or (if it is in a body) pull it out and move it alone (D4). */
export function movesForPiece(p: Position, i: number): Move[] {
  const out: Move[] = [];
  if (!p.alive[i] || p.owner[i] !== p.toMove) return out;
  const body = bodyOf(p, i);
  const inBody = body.length > 1;
  const side = p.owner[i] as Side;
  for (const frame of SHAPES[p.shape[i]].move) {
    const [dr, dc] = vecOf(side, frame);
    for (let d = 1; d <= 2; d++) {
      if (!inb(p.row[i] + dr * d, p.col[i] + dc * d)) break;    // the selected piece must stay on the board
      if (!bodyPathClear(p, body, dr, dc, d)) break;             // a blocked first step blocks the second
      out.push({ piece: i, dr, dc, dist: d as 1 | 2, solo: false });
    }
    if (!inBody) continue;
    for (let d = 1; d <= 2; d++) {
      const rr = p.row[i] + dr * d, cc = p.col[i] + dc * d;
      if (!inb(rr, cc) || p.board[cellOf(rr, cc)] >= 0) break;   // alone, every cell on the way must be empty
      out.push({ piece: i, dr, dc, dist: d as 1 | 2, solo: true });
    }
  }
  return out;
}

/** All moves for the side to move, ignoring ko. */
export function candidateMoves(p: Position): Move[] {
  const out: Move[] = [];
  for (let i = 0; i < MAX_PIECES; i++) if (p.alive[i] && p.owner[i] === p.toMove) out.push(...movesForPiece(p, i));
  return out;
}

export function sameMove(a: Move, b: Move): boolean {
  return a.piece === b.piece && a.dr === b.dr && a.dc === b.dc && a.dist === b.dist && a.solo === b.solo;
}
